import { useAuthFrontendApis } from "@propelauth/frontend-apis-react";
import { useOrgHelper } from "@propelauth/react";
import { Globe, Upload } from "lucide-react";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Avatar, AvatarFallback, AvatarImage } from "../../../../components/ui/avatar";
import { Button } from "../../../../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../../../../components/ui/dialog";
import { Input } from "../../../../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../../components/ui/select";
import { Switch } from "../../../../components/ui/switch";
import { queryClient } from "../../../../hooks/QueryHooks";
import { updateWorkspace } from "../../../../services/workspace/workspaceEndpoints";
import useHeaderStore from "../../../../store/layout/headerStore";
import useWorkspaceStore from "../../../../store/workspace/workspaceStore";
import notify from "../../../../utils/notify";
import { ModelSelector } from "../../../../components/agno/ModelSelector";
import {
  useWorkspaceAISettings,
  useUpdateWorkspaceAISettings,
} from "../../../../services/ai/aiModelHooks";
import BusinessContextPage from "../business-context/BusinessContextPage";
import WorkspaceMembersPage from "../workspace-members/WorkspaceMembersPage";

const TIMEZONE_OPTIONS: Array<{ value: string; label: string }> = [
  { value: "America/Los_Angeles", label: "Pacific Time (PT)" },
  { value: "America/Denver", label: "Mountain Time (MT)" },
  { value: "America/Chicago", label: "Central Time (CT)" },
  { value: "America/New_York", label: "Eastern Time (ET)" },
  { value: "UTC", label: "UTC" },
  { value: "Europe/London", label: "Greenwich Mean Time (GMT)" },
  { value: "Europe/Berlin", label: "Central European Time (CET)" },
  { value: "Asia/Kolkata", label: "India Standard Time (IST)" },
];

const CURRENCY_OPTIONS: Array<{ value: string; label: string }> = [
  { value: "USD", label: "USD ($)" },
  { value: "EUR", label: "EUR (\u20AC)" },
  { value: "GBP", label: "GBP (\u00A3)" },
];

type WorkspaceSettingsPageProps = {
  embedded?: boolean;
  showMembers?: boolean;
  focusSection?: "general" | "members";
};

type SettingRowProps = {
  label: string;
  description?: string;
  children: React.ReactNode;
};

const SettingRow = ({ label, description, children }: SettingRowProps) => (
  <div className="flex items-center justify-between gap-8 py-3">
    <div className="min-w-0">
      <p className="text-[13px] text-foreground">{label}</p>
      {description ? <p className="mt-0.5 text-[11px] text-muted-foreground">{description}</p> : null}
    </div>
    <div className="shrink-0">{children}</div>
  </div>
);

const WorkspaceSettingsPage: React.FC<WorkspaceSettingsPageProps> = ({
  embedded = false,
  showMembers = false,
  focusSection = "general",
}) => {
  const { setHeaderContent, resetHeader } = useHeaderStore();
  const authApis = useAuthFrontendApis();
  const { orgHelper } = useOrgHelper();

  const workspace = useWorkspaceStore((state) => state.workspace);
  const setWorkspace = useWorkspaceStore((state) => state.setWorkspace);

  const workspaceId = workspace?.id as string | undefined;
  const { data: aiSettings } = useWorkspaceAISettings({ workspaceId });
  const updateAISettings = useUpdateWorkspaceAISettings(workspaceId);

  const [workspaceName, setWorkspaceName] = useState("");
  const [timezone, setTimezone] = useState<string>("");
  const [trackingDomain, setTrackingDomain] = useState("");
  const [currency, setCurrency] = useState<string>("USD");
  const [guideEnabled, setGuideEnabled] = useState(false);
  const [savingKey, setSavingKey] = useState<string | null>(null);
  const logoFileInputRef = useRef<HTMLInputElement | null>(null);
  const [openBcSection, setOpenBcSection] = useState<string | null>(null);

  const timezoneOptions = useMemo(() => {
    if (!timezone || TIMEZONE_OPTIONS.some((option) => option.value === timezone)) {
      return TIMEZONE_OPTIONS;
    }

    return [{ value: timezone, label: timezone }, ...TIMEZONE_OPTIONS];
  }, [timezone]);

  const currencyOptions = useMemo(() => {
    if (!currency || CURRENCY_OPTIONS.some((option) => option.value === currency)) {
      return CURRENCY_OPTIONS;
    }

    return [{ value: currency, label: currency }, ...CURRENCY_OPTIONS];
  }, [currency]);

  const orgId = useMemo(() => {
    const uniqueIdentifier = workspace?.unique_identifier;
    if (!uniqueIdentifier) return null;
    const orgs: any[] = orgHelper?.getOrgs?.() ?? [];
    const match = orgs.find((org: any) => org?.orgMetadata?.unique_identifier === uniqueIdentifier);
    return match?.orgId ?? null;
  }, [orgHelper, workspace?.unique_identifier]);

  const orgName = useMemo(() => {
    const uniqueIdentifier = workspace?.unique_identifier;
    if (!uniqueIdentifier) return null;
    const orgs: any[] = orgHelper?.getOrgs?.() ?? [];
    const match = orgs.find((org: any) => org?.orgMetadata?.unique_identifier === uniqueIdentifier);
    return (match?.orgName ?? match?.name ?? null) as string | null;
  }, [orgHelper, workspace?.unique_identifier]);

  useEffect(() => {
    if (!showMembers || focusSection !== "members") return;
    const element = document.getElementById("workspace-members-section");
    if (!element) return;
    requestAnimationFrame(() => element.scrollIntoView({ behavior: "smooth", block: "start" }));
  }, [focusSection, showMembers]);

  useEffect(() => {
    if (embedded) return undefined;
    resetHeader();
    setHeaderContent({
      title: "General Settings",
      description: "Manage your workspace settings",
    });

    return () => resetHeader();
  }, [embedded, resetHeader, setHeaderContent]);

  useEffect(() => {
    setWorkspaceName(workspace?.workspace_name ?? orgName ?? "");
    setTimezone(workspace?.workspace_time_zone ?? "");
    setTrackingDomain(workspace?.tracking_website_domain ?? "");
    setCurrency(workspace?.workspace_default_currency ?? "USD");
    setGuideEnabled(!(workspace?.getting_started_guide_dismissed ?? false));
  }, [
    workspace?.workspace_name,
    workspace?.workspace_time_zone,
    workspace?.tracking_website_domain,
    workspace?.workspace_default_currency,
    workspace?.getting_started_guide_dismissed,
    orgName,
  ]);

  const patchWorkspaceCache = (patch: Record<string, any>) => {
    if (!workspace?.id) return;

    queryClient.setQueryData(["workspace", workspace.id], (oldData: any) => ({
      ...oldData,
      data: {
        ...oldData?.data,
        ...patch,
      },
    }));

    setWorkspace({ ...workspace, ...patch });
  };

  const runWithSaving = async (key: string, callback: () => Promise<void>) => {
    setSavingKey(key);
    try {
      await callback();
    } finally {
      setSavingKey(null);
    }
  };

  const saveWorkspaceName = async () => {
    if (!workspace?.id) return;

    const nextName = workspaceName.trim();
    if (!nextName) {
      notify({ type: "failure", heading: "Invalid workspace name", messageList: ["Workspace name cannot be empty."] });
      return;
    }

    if (nextName === (workspace?.workspace_name ?? "")) {
      notify({ type: "info", messageList: ["No changes to save."] });
      return;
    }

    await runWithSaving("workspace-name", async () => {
      if (!orgId) {
        notify({ type: "failure", heading: "Unable to update workspace name", messageList: ["Workspace org not found."] });
        return;
      }

      const response = await authApis.updateOrgSettings({ org_id: orgId, name: nextName });
      if (!response.ok) {
        notify({ type: "failure", heading: "Unable to update workspace name", messageList: ["Please try again later."] });
        return;
      }

      patchWorkspaceCache({ workspace_name: nextName });
      notify({ type: "success", messageList: ["Workspace name updated."] });
    });
  };

  const saveTrackingDomain = async () => {
    if (!workspace?.id) return;

    const nextDomain = trackingDomain.trim().toLowerCase().replace(/\/+$/, "");
    if (!nextDomain) {
      notify({ type: "failure", heading: "Invalid website domain", messageList: ["Website domain cannot be empty."] });
      return;
    }

    // Validate domain format: must be a valid domain without protocol or path
    const domainRegex = /^(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z]{2,}$/;
    if (!domainRegex.test(nextDomain)) {
      notify({
        type: "failure",
        heading: "Invalid domain",
        messageList: ["Enter a valid domain (e.g. example.com). Do not include http:// or paths."],
      });
      return;
    }

    if (nextDomain === (workspace?.tracking_website_domain ?? "")) {
      notify({ type: "info", messageList: ["No changes to save."] });
      return;
    }

    await runWithSaving("website-domain", async () => {
      try {
        await updateWorkspace({
          workspace_id: workspace.id,
          tracking_website_domain: nextDomain,
        });
        patchWorkspaceCache({ tracking_website_domain: nextDomain });
        notify({ type: "success", messageList: ["Website domain updated."] });
      } catch {
        notify({ type: "failure", heading: "Unable to update website domain", messageList: ["Please try again later."] });
      }
    });
  };

  const saveTimezone = async () => {
    if (!workspace?.id) return;

    if (!timezone) {
      notify({ type: "failure", heading: "Invalid timezone", messageList: ["Timezone is required."] });
      return;
    }

    if (timezone === (workspace?.workspace_time_zone ?? "")) {
      notify({ type: "info", messageList: ["No changes to save."] });
      return;
    }

    await runWithSaving("timezone", async () => {
      try {
        const response = await updateWorkspace({
          workspace_id: workspace.id,
          workspace_time_zone: timezone,
        });
        patchWorkspaceCache({
          ...(response?.data ? response.data : {}),
          workspace_time_zone: timezone,
        });
        notify({ type: "success", messageList: ["Timezone updated."] });
      } catch {
        notify({ type: "failure", heading: "Unable to update timezone", messageList: ["Please try again later."] });
      }
    });
  };

  const saveCurrency = async () => {
    if (!workspace?.id) return;

    if (!currency) {
      notify({ type: "failure", heading: "Invalid currency", messageList: ["Currency is required."] });
      return;
    }

    if (currency === (workspace?.workspace_default_currency ?? "")) {
      notify({ type: "info", messageList: ["No changes to save."] });
      return;
    }

    await runWithSaving("currency", async () => {
      try {
        const response = await updateWorkspace({
          workspace_id: workspace.id,
          workspace_default_currency: currency,
        });
        patchWorkspaceCache({
          ...(response?.data ? response.data : {}),
          workspace_default_currency: currency,
        });
        notify({ type: "success", messageList: ["Default currency updated."] });
      } catch {
        notify({ type: "failure", heading: "Unable to update currency", messageList: ["Please try again later."] });
      }
    });
  };

  const handleLogoFileSelected = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    event.target.value = "";
    if (!selectedFile || !workspace?.id) return;

    const allowedTypes = new Set(["image/jpeg", "image/png", "image/gif", "image/webp", "image/svg+xml"]);
    if (!allowedTypes.has(selectedFile.type)) {
      notify({ type: "failure", heading: "Unsupported file type", messageList: ["Use PNG, JPG, GIF, WEBP, or SVG images."] });
      return;
    }

    if (selectedFile.size > 2 * 1024 * 1024) {
      notify({ type: "failure", heading: "Image too large", messageList: ["Logo must be 2 MB or smaller."] });
      return;
    }

    await runWithSaving("logo", async () => {
      try {
        const formData = new FormData();
        formData.append("logo", selectedFile);
        formData.append("workspace_id", workspace.id);

        const { data } = await (await import("../../../../services/config")).authorizedApiClient.put(
          `/workspace/${workspace.id}/logo`,
          formData,
          { headers: { "Content-Type": "multipart/form-data" } },
        );

        patchWorkspaceCache({ workspace_logo: data.logo_url });
        notify({ type: "success", messageList: ["Workspace logo updated."] });
      } catch {
        notify({ type: "failure", heading: "Unable to update logo", messageList: ["Please try again later."] });
      }
    });
  };

  const saveGuideEnabled = async (enabled: boolean) => {
    if (!workspace?.id) return;
    setGuideEnabled(enabled);

    await runWithSaving("guide", async () => {
      try {
        await updateWorkspace({
          workspace_id: workspace.id,
          getting_started_guide_dismissed: !enabled,
        });
        patchWorkspaceCache({ getting_started_guide_dismissed: !enabled });
        notify({ type: "success", messageList: [enabled ? "Getting started guide enabled." : "Getting started guide dismissed."] });
      } catch {
        notify({ type: "failure", heading: "Unable to update guide setting", messageList: ["Please try again later."] });
        setGuideEnabled(!enabled);
      }
    });
  };

  const BC_ITEMS = [
    { label: "Company Profile", description: "Your company name, website, and description", action: "Edit", section: "company-profile" },
    { label: "Products", description: "Products and services you offer", action: "Manage", section: "products" },
    { label: "Features", description: "Product features mapped to events and metrics", action: "Manage", section: "features" },
    { label: "Event Catalog", description: "Events that track user and account activity", action: "Manage", section: "events" },
    { label: "Lifecycle Stages", description: "Stages in your customer journey", action: "Manage", section: "lifecycle" },
  ] as const;

  return (
    <div className={`relative ${embedded ? "" : "mx-auto max-w-5xl px-6 py-6"}`}>
      {savingKey ? (
        <div className="absolute inset-0 z-20 flex items-center justify-center bg-background/70 backdrop-blur-[1px]">
          <span className="h-6 w-6 animate-spin rounded-full border-2 border-primary/30 border-t-primary" />
        </div>
      ) : null}

      <div className={`space-y-8 ${embedded ? "" : "mt-6"}`}>
        <div id="workspace-general-section">
          <h1 className="text-lg font-medium text-foreground">General</h1>
          <p className="mt-1 text-[13px] text-muted-foreground">Manage your workspace settings</p>
        </div>

        <div className="divide-y divide-border">
          <SettingRow label="Workspace Name">
            <div className="flex items-center gap-2">
              <Input
                value={workspaceName}
                onChange={(event) => setWorkspaceName(event.target.value)}
                className="h-8 w-56 text-[13px]"
              />
              <Button
                size="sm"
                variant="outline"
                className="h-8 text-[12px]"
                onClick={() => {
                  saveWorkspaceName().catch(() => {});
                }}
                disabled={savingKey === "workspace-name"}
              >
                Save
              </Button>
            </div>
          </SettingRow>

          <SettingRow label="Logo" description="Displayed in sidebar and emails">
            <div className="flex items-center gap-3">
              <Avatar className="h-8 w-8">
                {workspace?.workspace_logo ? <AvatarImage src={workspace.workspace_logo} alt={workspaceName || "Workspace logo"} /> : null}
                <AvatarFallback className="bg-primary/10 text-[11px] font-medium text-primary">
                  {(workspaceName || "W").charAt(0).toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <Button
                size="sm"
                variant="outline"
                className="h-8 text-[12px]"
                onClick={() => logoFileInputRef.current?.click()}
                disabled={savingKey === "logo"}
              >
                <Upload className="mr-1.5 h-3 w-3" />
                Upload
              </Button>
              <input
                ref={logoFileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/gif,image/webp,image/svg+xml"
                className="hidden"
                onChange={(e) => { handleLogoFileSelected(e).catch(() => {}); }}
              />
            </div>
          </SettingRow>

          <SettingRow label="Website Domain" description="Used for tracking and identification">
            <div className="flex items-center gap-2">
              <Input
                value={trackingDomain}
                onChange={(event) => setTrackingDomain(event.target.value)}
                onBlur={() => setTrackingDomain((d) => d.trim().toLowerCase().replace(/^https?:\/\//, "").replace(/\/+$/, ""))}
                placeholder="example.com"
                className="h-8 w-56 text-[13px]"
              />
              <Button
                size="sm"
                variant="outline"
                className="h-8 text-[12px]"
                onClick={() => {
                  saveTrackingDomain().catch(() => {});
                }}
                disabled={savingKey === "website-domain"}
              >
                Save
              </Button>
            </div>
          </SettingRow>

          <SettingRow label="Timezone">
            <div className="flex items-center gap-2">
              <Select value={timezone} onValueChange={setTimezone}>
                <SelectTrigger className="h-8 w-56 text-[13px]">
                  <div className="flex items-center gap-1.5">
                    <Globe className="h-3.5 w-3.5 text-muted-foreground" />
                    <SelectValue placeholder="Select timezone" />
                  </div>
                </SelectTrigger>
                <SelectContent>
                  {timezoneOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value} className="text-[12px]">
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                size="sm"
                variant="outline"
                className="h-8 text-[12px]"
                onClick={() => {
                  saveTimezone().catch(() => {});
                }}
                disabled={savingKey === "timezone"}
              >
                Save
              </Button>
            </div>
          </SettingRow>

          <SettingRow label="Default Currency">
            <div className="flex items-center gap-2">
              <Select value={currency} onValueChange={setCurrency}>
                <SelectTrigger className="h-8 w-32 text-[13px]">
                  <SelectValue placeholder="Currency" />
                </SelectTrigger>
                <SelectContent>
                  {currencyOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value} className="text-[12px]">
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                size="sm"
                variant="outline"
                className="h-8 text-[12px]"
                onClick={() => {
                  saveCurrency().catch(() => {});
                }}
                disabled={savingKey === "currency"}
              >
                Save
              </Button>
            </div>
          </SettingRow>

          <SettingRow label="Getting Started Guide" description="Show onboarding guide for new members">
            <Switch
              checked={guideEnabled}
              onCheckedChange={(checked) => { saveGuideEnabled(checked).catch(() => {}); }}
              disabled={savingKey === "guide"}
            />
          </SettingRow>
        </div>

        <div>
          <h2 className="mb-1 text-[13px] font-medium text-foreground">Business Context</h2>
          <p className="mb-4 text-[11px] text-muted-foreground">Help AI understand your business by providing context about your company, products, and customer journey</p>
          <div className="divide-y divide-border">
            {BC_ITEMS.map((item) => (
              <div key={item.label} className="flex items-center justify-between py-2.5">
                <div className="min-w-0">
                  <span className="text-[13px] text-foreground">{item.label}</span>
                  <p className="text-[11px] text-muted-foreground">{item.description}</p>
                </div>
                <button
                  type="button"
                  className="shrink-0 text-[12px] text-muted-foreground transition-colors hover:text-foreground"
                  onClick={() => setOpenBcSection(item.section)}
                >
                  {item.action} →
                </button>
              </div>
            ))}
          </div>
        </div>

        <div>
          <h2 className="mb-1 text-[13px] font-medium text-foreground">AI Models</h2>
          <p className="mb-4 text-[11px] text-muted-foreground">Choose the default models for the assistant, signals and health scoring, and the lighter model used by background processing</p>
          <div className="divide-y divide-border">
            <SettingRow label="Default model" description="Used by the assistant, signals and health scoring unless overridden">
              <ModelSelector
                surface="agent"
                workspaceId={workspaceId}
                value={aiSettings?.default_model ?? undefined}
                onChange={(modelKey) => {
                  updateAISettings.mutate(
                    { default_model: modelKey },
                    { onError: () => notify({ type: "failure", heading: "Failed to update default model", messageList: ["Please try again later."] }) },
                  );
                }}
              />
            </SettingRow>
            <SettingRow label="Background model" description="Lighter model used by background processing (extraction, automations)">
              <ModelSelector
                surface="background"
                workspaceId={workspaceId}
                value={aiSettings?.background_model ?? undefined}
                onChange={(modelKey) => {
                  updateAISettings.mutate(
                    { background_model: modelKey },
                    { onError: () => notify({ type: "failure", heading: "Failed to update background model", messageList: ["Please try again later."] }) },
                  );
                }}
              />
            </SettingRow>
          </div>
        </div>

        {showMembers ? (
          <div id="workspace-members-section">
            <WorkspaceMembersPage embedded />
          </div>
        ) : null}
      </div>

      {/* Business Context Dialog */}
      <Dialog open={!!openBcSection} onOpenChange={(open) => { if (!open) setOpenBcSection(null); }}>
        <DialogContent
          className="max-w-4xl max-h-[85vh] overflow-y-auto"
          onPointerDownOutside={(e) => {
            const target = e.target as HTMLElement;
            if (target?.closest?.(".mantine-Menu-dropdown, .mantine-Popover-dropdown, .mantine-Select-dropdown, [data-mantine-portal], [data-radix-popper-content-wrapper]")) {
              e.preventDefault();
            }
          }}
          onInteractOutside={(e) => {
            const target = e.target as HTMLElement;
            if (target?.closest?.(".mantine-Menu-dropdown, .mantine-Popover-dropdown, .mantine-Select-dropdown, [data-mantine-portal], [data-radix-popper-content-wrapper]")) {
              e.preventDefault();
            }
          }}
        >
          <DialogHeader>
            <DialogTitle>
              {BC_ITEMS.find((i) => i.section === openBcSection)?.label ?? "Business Context"}
            </DialogTitle>
            <DialogDescription>
              {BC_ITEMS.find((i) => i.section === openBcSection)?.description}
            </DialogDescription>
          </DialogHeader>
          {openBcSection && (
            <BusinessContextPage section={openBcSection as any} embedded />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default WorkspaceSettingsPage;
